import React, { useEffect, useState } from "react";

import AntennaBox3D from "./AntennaBox3D";
import { useAuth } from "../../contexts/AuthContext";
import { boxDrawingFor } from "../../lib/antennaGeometry";
import { fmtAgo } from "../../lib/format";
import { type TagHeatMapData, fetchTagHeatMap } from "../../services/tagApi";

/**
 * Where a tag is being seen inside the box, drawn as the box.
 *
 * The container is drawn isometrically with the antennas where they physically
 * sit. The numbered grid that used to sit beside it is gone (Jason
 * 2026-09-17): two views of the same facts, and the one that looks like the
 * box is the one people read.
 *
 * Most boxes use the SB2204A000 drawing — three shelf bays per wall, four
 * antennas high, eight flat plates, antenna 1 on the ceiling at the door.
 * SB093 is surveyed instead and draws its own measured layout. A recorded
 * shelf type (`mttr.smartbox_antenna_layout`) is what selects between them.
 *
 * ON-HAND ONLY, enforced server-side. A departed tag has a last known antenna
 * and drawing it would assert a position the box no longer supports.
 *
 * WHERE THE HEAT COMES FROM. The server reads the BOX where it can, and the
 * box keeps every antenna that saw the tag plus that antenna's RSSI — so each
 * lamp is shaded by signal strength and this is a genuine heat map. The
 * scanner throws all of that away when it publishes (one entry per EPC,
 * best-RSSI antenna only), so the cloud fallback can light exactly one lamp
 * per tag. `source` says which you are looking at, and the component adapts to
 * whichever it is given rather than being told.
 */
export const TagHeatMap: React.FC<{ epc: string }> = ({ epc }) => {
  const { token } = useAuth();
  const [data, setData] = useState<TagHeatMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchTagHeatMap(epc, token)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [epc, token]);

  if (loading) return <div className="text-sm text-secondary-500">Loading…</div>;
  if (error) {
    return (
      <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
        {error}
      </div>
    );
  }
  if (!data) return null;

  if (!data.on_hand) {
    return (
      <div className="text-sm text-secondary-600">
        This tag isn&apos;t on hand, so there&apos;s nothing to place — a heat
        map is only drawn for tags in a box&apos;s current scan.
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs text-secondary-500 mb-3">
        {data.smartbox_code ?? data.scu_name}
        {data.scan_end_utc && <> · scanned {fmtAgo(data.scan_end_utc)} ago</>}
        {/* Where the numbers came from. Never blended away: an edge read is
            per-antenna and minutes old, while the cloud keeps one antenna per
            tag and, for a box outside the ingest rotation, can be months
            stale. Those are different enough that the reader should know. */}
        {data.source === "edge" ? (
          <>
            {" · "}
            <span className="text-success-700">read from the box</span>
            {data.scanner_version && <> (scanner {data.scanner_version})</>}
          </>
        ) : (
          <>
            {" · "}
            <span title={
              "The box could not be reached, so this is the cloud's copy: one "
              + "antenna per tag, and only as fresh as the last scan that "
              + "reached the database."
            }>
              from cloud scan data
            </span>
          </>
        )}
      </div>

      {data.readers.map((r) => {
        // Reader count matters: a multi-reader box is a different shape, not
        // N copies of the 20 ft one, so it gets no drawing rather than a
        // confident wrong one.
        const drawing = boxDrawingFor(data.layout_key, r.port_count, data.readers.length);
        const lit = new Set(r.detected);
        // Per-antenna signal, when the box itself was read. The cloud path has
        // no equivalent -- it stores one best-RSSI antenna per tag -- so this
        // is simply absent there and the drawing lights a single lamp.
        const reads = r.reads?.length
          ? new Map(r.reads.map((x) => [x.antenna, { reads: x.reads, best_rssi: x.best_rssi }]))
          : undefined;
        // Only the edge path knows this. `undefined` (cloud) makes the drawing
        // fall back to the layout's hardcoded unwired list rather than
        // claiming every antenna is enabled.
        const disabled = r.disabled ? new Set(r.disabled) : undefined;
        return (
          <div key={r.reader_id} className="mb-4 last:mb-0">
            <div className="flex items-baseline gap-2 mb-1">
              {data.readers.length > 1 && (
                <span className="text-xs font-medium text-secondary-700">
                  Reader {r.reader_id}
                </span>
              )}
              <span
                className="text-[11px] text-secondary-400"
                title={
                  drawing.key === "txam-sb093"
                    ? "Layout SURVEYED on this box, not read off a drawing: 28 "
                      + "antennas on a 32-port reader (ports 11, 15, 17, 21 are "
                      + "unwired), 10 on the ceiling and 18 on the racks. Hover an "
                      + "antenna for its panel label and reference-sticker EPC. "
                      + "Antenna 1 is at the cargo end here, not the door."
                    : "Layout from the SB2204A000 drawing: three shelf bays per wall, "
                      + "four antennas high, plus eight flat plates. Antenna 1 is on the "
                      + "ceiling at the door end."
                }
              >
                {drawing.label}
              </span>
            </div>

            {drawing.key ? (
              <div className="inline-block rounded border border-secondary-200 bg-white p-2">
                <AntennaBox3D lit={lit} layoutKey={drawing.key} reads={reads} disabled={disabled} />
              </div>
            ) : (
              /* No drawing for this shape (the 3-reader 40 ft box). Listing the
                 ports beats an empty panel. */
              <div className="text-xs text-secondary-600">
                {r.port_count} ports on this reader — no box drawing for this
                shape yet.
              </div>
            )}

            <div className="text-[11px] text-secondary-500 mt-1">
              Read by {r.detected.length} of {r.port_count} antenna
              {r.port_count === 1 ? "" : "s"}
              {r.detected.length > 0 && <> · {r.detected.join(", ")}</>}
              <span className="ml-2 font-mono text-secondary-400">{r.detected_mask}</span>
            </div>

            {/* Surveyed boxes know which ports have no antenna on them. Saying
                so is the difference between a gap in the port list meaning a
                fault and meaning nothing at all. */}
            {r.reader_disabled && (
              <div className="text-[11px] text-warning-700 mt-0.5">
                This reader is switched off on the box — every antenna under it
                is silent by configuration, not by fault.
              </div>
            )}
            {!!r.disabled?.length && (
              <div className="text-[11px] text-secondary-400 mt-0.5">
                Disabled on this box: {r.disabled.join(", ")} — switched off, so
                reading nothing is expected rather than a fault.
              </div>
            )}
            {drawing.key === "txam-sb093" && (
              <div className="text-[11px] text-secondary-400 mt-0.5">
                Ports 11, 15, 17 and 21 have no antenna wired — drawn outside
                the container, since they have no position in it.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TagHeatMap;
